import React from "react";
import styled from "@emotion/styled";
import Button from "~/components/button";
import "./FormBottomRow.scss";
import { Route } from "~/routeTypes";
import ErrorMessage from "~/components/error-message";
import { InlineProgress } from "~/components/inline-progress";
import cn from "classnames";

// The spinner sits beside the label rather than replacing it, so the button
// keeps its width mid-request. MDC lays the button out as a flex row but gives
// `.mdc-button__label` no cross-axis alignment of its own, and the spinner's
// box is taller than the text line -- so centre the label explicitly here
// instead of letting it fall to the row's baseline.
const SubmitButton = styled(Button)`
  &.mdc-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .mdc-button__label {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }
`;

const SubmitSpinner = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  line-height: 1;
`;

type FormBottomRowProps = {
  errorMessage: string | null;
  route?: Route;
  buttonText: string;
  cancelButtonText?: string;
  onCancel?: () => void;
  onSubmit?: (e?: any) => void;
  cancelButtonClass?: string;
  /**
   * Pass the in-flight flag of whatever mutation this form submits. The submit
   * button shows a spinner and both buttons disable, so a pending request
   * cannot be fired twice or cancelled halfway. Optional: omitting it leaves
   * the buttons always enabled.
   */
  loading?: boolean;
};

const FormBottomRow: React.FC<FormBottomRowProps> = ({
  errorMessage,
  route,
  buttonText,
  onCancel,
  onSubmit,
  cancelButtonText,
  children,
  cancelButtonClass,
  loading,
}) => {
  return (
    <div className={"form-bottom-row"}>
      <ErrorMessage errorMessage={errorMessage || null} />
      <section className="actions">
        {buttonText != "" && (
          <SubmitButton
            primary
            data-testid="submit-button"
            className="form-bottom-submit"
            onClick={onSubmit}
            disabled={loading}
          >
            {loading && (
              <SubmitSpinner data-testid="submit-spinner">
                <InlineProgress size="xsmall" />
              </SubmitSpinner>
            )}
            {buttonText}
          </SubmitButton>
        )}
        {children}
        <Button
          type="button"
          route={route}
          className={cn("form-bottom-cancel", cancelButtonClass)}
          onClick={onCancel}
          disabled={loading}
        >
          {cancelButtonText || "Cancel"}
        </Button>
      </section>
    </div>
  );
};

export default FormBottomRow;
